import { useTheme } from '@mui/material';
import Highcharts, { TooltipFormatterCallbackFunction } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useEffect, useRef } from 'react';

interface IProps {
  height?: number;
  categories?: Array<string>;
  tooltipFormatter?: TooltipFormatterCallbackFunction;
  dataPropertyKeys?: Array<string>;
  loading?: boolean;
  series?: Highcharts.SeriesOptionsType[];
  xAxis?: Highcharts.XAxisOptions;
  yAxis?: Highcharts.YAxisOptions;
  plotOptions?: Highcharts.PlotOptions;
  legend?: Highcharts.LegendOptions;
  tooltip?: Highcharts.Tooltip;
}

const AreaChart = ({
  height = 400,
  categories = [],
  tooltipFormatter,
  series = [],
  xAxis = {},
  yAxis = {},
  plotOptions = {},
  legend = {},
  dataPropertyKeys,
  loading,
  tooltip,
}: IProps) => {
  const {
    palette: { primary, text, tooltipFill },
  } = useTheme();

  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);

  useEffect(() => {
    if (!chartComponentRef.current?.chart) return;
    if (loading) {
      chartComponentRef.current?.chart.showLoading('Loading data...');
      return;
    }

    chartComponentRef.current?.chart.hideLoading();
  }, [chartComponentRef.current?.chart, loading]);

  const options: Highcharts.Options = {
    chart: {
      type: 'area',
      height: height,
    },
    title: {
      text: '',
    },
    legend: {
      enabled: false,
      ...legend,
    },
    xAxis: {
      labels: {
        style: {
          color: text.secondary,
        },
      },
      lineColor: text.secondary,
      tickColor: text.secondary,
      categories: categories,
      showLastLabel: true,
      endOnTick: true,
      startOnTick: true,
      tickmarkPlacement: 'on',
      ...xAxis,
    },
    yAxis: {
      title: {
        text: '',
      },
      labels: {
        style: {
          color: text.secondary,
        },
      },
      lineColor: text.secondary,
      tickColor: text.secondary,
      ...yAxis,
    },
    tooltip: {
      useHTML: true,
      backgroundColor: tooltipFill.main,
      headerFormat: '',
      formatter: tooltipFormatter,
      style: {
        color: '#fff',
      },
      padding: 6,
      ...tooltip,
    },
    credits: {
      enabled: true,
    },
    plotOptions: {
      series: {
        pointPlacement: 'on',
        marker: {
          enabled: false,
          fillColor: primary.main,
          lineWidth: 0.5,
          radius: 5,
        },
        keys: dataPropertyKeys,
      },
      ...plotOptions,
    },
    series: series,
  };
  return <HighchartsReact highcharts={Highcharts} options={options} ref={chartComponentRef} />;
};

export default AreaChart;
